import { motion } from "framer-motion";
import { Recycle, Zap, Package, RefreshCcw, Globe2, Infinity as InfinityIcon } from "lucide-react";
import { useSiteImages } from "@/hooks/useSiteImages";

const pillars = [
  {
    icon: Package,
    title: "Eco-Friendly Packaging",
    desc: "Every bottle is designed for a second life — 100% recyclable today, with an active transition to full glass.",
  },
  {
    icon: RefreshCcw,
    title: "Glass Bottle Reverse Logistics",
    desc: "Our glass bottles return to us on the same routes they left on — cleaned, refilled, and back on the table.",
  },
  {
    icon: Recycle,
    title: "Recycling Initiatives",
    desc: "We invest in local recycling partners so what leaves the bottle stays in the loop, not in the landfill.",
  },
  {
    icon: Zap,
    title: "Conserving Water Resources",
    desc: "Because we harvest from the sky, every litre we bottle is a litre left in the ground for future generations.",
  },
  {
    icon: Globe2,
    title: "Cleaner Production, Cleaner Planet",
    desc: "Carefully managed energy use, zero-reject processing, and a facility footprint that shrinks with every cycle.",
  },
  {
    icon: InfinityIcon,
    title: "Innovative & Infinite Source",
    desc: "The atmosphere holds over 12,900 cubic kilometres of moisture — an endless, replenishing wellspring for us all.",
  },
];

export const Sustainability = () => {
  const imgs = useSiteImages();
  return (
  <section id="sustainability" data-testid="sustainability-section" className="py-24 md:py-32 lg:py-40 bg-[#F5F5F7]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="text-center mb-16 md:mb-24 max-w-3xl mx-auto"
      >
        <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">Responsible by Design</p>
        <h1 data-testid="sustainability-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-medium text-[#1D1D1F]">
          Sustainability isn't a choice we made. It's how we were built.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[#86868B]">
          Every step of our process was designed around a simple promise: take nothing from
          the ground, waste nothing in production, and package everything for a second life.
          The result is water with the lightest footprint on earth.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 1.03 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1 }}
        className="rounded-3xl overflow-hidden mb-14 md:mb-20"
      >
        <img src={imgs.sustainability} alt="Dew drops on forest leaves" loading="lazy" decoding="async" className="w-full h-[280px] md:h-[420px] object-cover" />
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {pillars.map((p, i) => (
          <motion.div
            key={p.title}
            data-testid={`sustainability-card-${i}`}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.12 }}
            className="bg-white rounded-3xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-transform duration-300 ease-out"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#2C3E35]/10 flex items-center justify-center mb-5">
              <p.icon className="w-6 h-6 text-[#2C3E35]" strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium text-[#1D1D1F] mb-2">{p.title}</h3>
            <p className="text-base leading-relaxed text-[#86868B]">{p.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
  );
};
